/**
 * Générateur — Automatisme : année, siècle, millénaire
 * Attendu ID : 1034 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève sait combien de jours
 * il y a dans une année (bissextile ou non), combien d'années il y a dans un
 * siècle, et dans un millénaire. »
 * Labels (cartes exerciseur) :
 *   - L'année, le siècle, le millénaire
 *
 * ── Plan de cartes (2026-08-22, cadrage prof) ──────────────────────────────
 * CINQ faits calendaires, purement mémoriels : 1 année (non bissextile) =
 * 365 jours ; 1 année bissextile = 366 jours ; 1 siècle = 100 ans ;
 * 1 millénaire = 1 000 ans ; 1 millénaire = 10 siècles (la déduction
 * immédiate, acceptée). HORS PÉRIMÈTRE : février, mois/semaines, la règle
 * des multiples de 4 (réservée au coup de pouce comme explication du 366),
 * le rattachement des dates aux siècles (histoire).
 * PRÉCAUTION A7 : jamais « année » nue avec 366 — toujours « année
 * (non bissextile) » ou « année bissextile » (une bissextile est aussi une
 * année). ERREURS CIBLES : l'échange 100 ↔ 1 000 (siècle/millénaire), le
 * décalage 365 ↔ 366, les 364/360 (52 semaines, le faux rond).
 * Domaine minuscule assumé : TIRAGE SANS REMISE par format.
 */
import {
    randomChoice, shuffle, tirageSansRemise,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1034;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'L\'année, le siècle, le millénaire';

/** Les CINQ faits (domaine fermé). `entite` s'écrit après « 1 »,
 *  `unite` après le nombre. */
const FAITS = [
    { id: 'annee', entite: 'année (non bissextile)', kFr: '365', unite: 'jours' },
    { id: 'bissextile', entite: 'année bissextile', kFr: '366', unite: 'jours' },
    { id: 'siecle', entite: 'siècle', kFr: '100', unite: 'ans' },
    { id: 'millenaire', entite: 'millénaire', kFr: '1 000', unite: 'ans' },
    { id: 'mille_siecles', entite: 'millénaire', kFr: '10', unite: 'siècles' },
];

/** Question directe par fait. */
const QUESTIONS = {
    annee: 'Combien de jours y a-t-il dans une année (non bissextile) ?',
    bissextile: 'Combien de jours y a-t-il dans une année bissextile ?',
    siecle: 'Combien d\'années y a-t-il dans un siècle ?',
    millenaire: 'Combien d\'années y a-t-il dans un millénaire ?',
    mille_siecles: 'Combien de siècles y a-t-il dans un millénaire ?',
};

/** Grilles à erreurs nommées : la cible toujours là, 2 tournants parmi 3. */
const GRILLES = {
    annee: {
        cible: { valeur: '366', code: 'decalage_bissextile' },
        tournants: [
            { valeur: '364', code: 'cinquante_deux_semaines' },
            { valeur: '360', code: 'faux_rond' },
            { valeur: '350', code: 'faux_rond' },
        ],
    },
    bissextile: {
        cible: { valeur: '365', code: 'decalage_bissextile' },
        tournants: [
            { valeur: '364', code: 'cinquante_deux_semaines' },
            { valeur: '360', code: 'faux_rond' },
            { valeur: '367', code: 'decalage_double' },
        ],
    },
    siecle: {
        cible: { valeur: '1 000', code: 'echange_millenaire' },
        tournants: [
            { valeur: '10', code: 'decennie' },
            { valeur: '10 000', code: 'rang_de_trop' },
            { valeur: '365', code: 'confusion_annee' },
        ],
    },
    millenaire: {
        cible: { valeur: '100', code: 'echange_siecle' },
        tournants: [
            { valeur: '10 000', code: 'rang_de_trop' },
            { valeur: '10', code: 'decennie' },
            { valeur: '365', code: 'confusion_annee' },
        ],
    },
    mille_siecles: {
        cible: { valeur: '100', code: 'echange_ans' },
        tournants: [
            { valeur: '1 000', code: 'echange_ans' },
            { valeur: '5', code: 'faux_rond' },
            { valeur: '20', code: 'faux_rond' },
        ],
    },
};

// ─── Format 1 : QCM (question, égalité, ou l'entité en réponse) ───────────

const finirQcm = (bonne, dis, texte, sousType) => {
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        opts(L1),
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis };
    return payload;
};

const ENTITES = ['année (non bissextile)', 'année bissextile', 'siècle', 'millénaire'];

const genQcm = () => {
    const f = randomChoice(FAITS);
    const g = GRILLES[f.id];
    const r = Math.random();
    if (r < 0.4) {
        // la question directe
        const dis = [g.cible, ...shuffle(g.tournants).slice(0, 2)];
        return finirQcm(f.kFr, dis, `<p><b>${QUESTIONS[f.id]}</b></p>`, 'question');
    }
    if (r < 0.75) {
        // l'égalité, deux sens
        const dis = [g.cible, ...shuffle(g.tournants).slice(0, 2)];
        const texte = Math.random() < 0.5
            ? `<p><b>Complète : 1 ${f.entite} = ... ${f.unite}</b></p>`
            : `<p><b>Complète : ... ${f.unite} = 1 ${f.entite}</b></p>`;
        return finirQcm(f.kFr, dis, texte, 'egalite');
    }
    // l'ENTITÉ en réponse : « 365 jours = 1 ... » — grille des 4 entités
    // (pour « 10 siècles = 1 ... », les choix restent pertinents)
    const bonne = f.entite;
    const dis = shuffle(ENTITES.filter((e) => e !== bonne)).slice(0, 3)
        .map((e) => ({ valeur: e, code: 'autre_entite' }));
    return finirQcm(bonne, dis, `<p><b>Complète : ${f.kFr} ${f.unite} = 1 ...</b></p>`, 'entite');
};

// ─── Format 2 : trous saisie ──────────────────────────────────────────────

/** Une ligne à un trou — le nombre, le 1, ou un mot (unité/entité simple).
 *  Les trous d'entité sont réservés à siècle/millénaire (un seul mot,
 *  sans parenthèse ni ambiguïté au clavier). */
const ligneEgalite = (f, forme) => {
    switch (forme) {
        case 'nombre_droite': return `1 ${f.entite} = {{${f.kFr}}} ${f.unite}`;
        case 'nombre_gauche': return `{{${f.kFr}}} ${f.unite} = 1 ${f.entite}`;
        case 'le_un': return `${f.kFr} ${f.unite} = {{1}} ${f.entite}`;
        case 'unite_gauche': return `${f.kFr} {{${f.unite}}} = 1 ${f.entite}`;
        default: return `${f.kFr} ${f.unite} = 1 {{${f.entite}}}`;   // entité (mot simple)
    }
};

const genTrouSaisie = () => {
    const variante = randomChoice(['egalites', 'egalites', 'mots']);
    const nb = randomChoice([1, 2, 2]);
    const faits = shuffle([...FAITS]).slice(0, nb);
    const lignes = faits.map((f) => {
        if (variante === 'egalites') {
            return ligneEgalite(f, randomChoice(['nombre_droite', 'nombre_gauche', 'le_un']));
        }
        // mots : l'unité manquante partout ; l'entité seulement si mot simple
        const formes = (f.entite === 'siècle' || f.entite === 'millénaire')
            ? ['unite_gauche', 'entite_droite']
            : ['unite_gauche'];
        return ligneEgalite(f, randomChoice(formes));
    });
    return makeTexteTrou(
        lignes.join('\n'),
        variante === 'egalites'
            ? '<p><b>Complète les égalités.</b></p>'
            // les candidats entre parenthèses (prof 2026-08-22) : l'élève
            // recopie le mot exact, l'orthographe et le pluriel ne sont pas
            // l'objet de l'automatisme
            : '<p><b>Complète avec le mot qui convient (jours, ans, siècles, siècle ou millénaire).</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 3 : V/F (pool fermé étiqueté à la main) ───────────────────────

// Miroirs conservés (arbitrage prof récurrent 1029/1031/1033 : lire
// l'égalité dans les deux sens fait partie du savoir, et les jumelles
// détectent les réponses au hasard).
const AFFIRMATIONS = [
    { t: '1 année (non bissextile) = 365 jours', v: true },
    { t: '1 année bissextile = 366 jours', v: true },
    { t: '1 siècle = 100 ans', v: true },
    { t: '1 millénaire = 1 000 ans', v: true },
    { t: '1 millénaire = 10 siècles', v: true },
    { t: '100 ans = 1 siècle', v: true },
    { t: '1 année bissextile = 365 jours', v: false },  // LE DÉCALAGE
    { t: '1 siècle = 1 000 ans', v: false },            // L'ÉCHANGE
    { t: '1 millénaire = 100 ans', v: false },
    { t: '1 année (non bissextile) = 360 jours', v: false },
    { t: '1 millénaire = 100 siècles', v: false },
];

const genVraiFaux = () => {
    for (let essai = 0; essai < 30; essai++) {
        const tirage = shuffle([...AFFIRMATIONS]).slice(0, 4);
        const nbVraies = tirage.filter((a) => a.v).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            tirage.map((a) => a.t),
            tirage.map((a) => (a.v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        [AFFIRMATIONS[0].t, AFFIRMATIONS[7].t, AFFIRMATIONS[3].t, AFFIRMATIONS[6].t],
        [0, 1, 0, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'CINQ faits mémoriels : 365 / 366 / 100 / 1 000 / 10 siècles — rien d\'autre',
        'jamais « année » nue avec 366 : toujours « (non bissextile) » ou « bissextile »',
        'hors périmètre : février, mois/semaines, la règle des multiples de 4, les dates→siècles',
        'domaine fermé assumé : tirage sans remise par format',
    ],
    calendrier_qcm_unique: {
        support: 'question directe, égalité deux sens, ou l\'entité en réponse',
        cibles: {
            decalage_bissextile: '365 ↔ 366 croisés — toujours présents sur les années',
            echange: '100 ↔ 1 000 croisés — toujours présents sur siècle/millénaire',
        },
        tournants: '2 parmi 3 : 364 (52 semaines), 360/350 (faux ronds), 10, 10 000, 365 (confusion d\'unité)',
        entite: 'grille des 4 entités (année non bissextile / bissextile / siècle / millénaire)',
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    calendrier_qcm_unique: genQcm,
    calendrier_texte_trou_saisie: genTrouSaisie,
    calendrier_tableau_vf: genVraiFaux,
};

const FORMAT_LABELS = {
    calendrier_qcm_unique: 'QCM',
    calendrier_texte_trou_saisie: 'Saisie',
    calendrier_tableau_vf: 'Vrai/Faux',
};

export const CATEGORIES = [
    {
        id: 'calendrier',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    // domaine minuscule (5 faits) : TIRAGE SANS REMISE par format
    const payload = tirageSansRemise(
        `gen_1034:${key}`,
        () => GENERATORS[key](),
        (p) => p.texte
            + JSON.stringify(p.contenu.phrase
                || (p.elements_additionnels?.elements?.[0]?.config?.lignes ?? ''))
            + JSON.stringify(p._meta.params?.sousType ?? ''),
        250,
    );
    payload._meta.generatorKey = key;
    return payload;
};
